import { CartLinesUpdateEvent } from '@shopify/events';
import '@theme/component-quantity-selector';
import { findSizeVariant, getSellingPlans, getPurchaseSelection, getBillingCadence, getSizeProduct, canPreviewThirtyTablets, loadLinkedProduct } from '@theme/barrier-purchase-model';

class BarrierPurchase extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.form = this.querySelector('[data-barrier-form]');
    this.status = this.querySelector('[data-purchase-status]');
    this.submitButton = this.form.querySelector('[type="submit"]');
    this.sticky = this.querySelector('[data-sticky]');
    this.stickySubmit = this.querySelector('[data-sticky-submit]');
    this.planSelect = this.querySelector('[data-selling-plan]');
    this.quantitySelector = this.querySelector('quantity-selector-component');
    this.quantityInput = this.quantitySelector.querySelector('[name="quantity"]');
    this.quantityFieldset = this.querySelector('[data-quantity-fieldset]');
    this.storageKey = `barrier-options-${this.dataset.product}-${window.Shopify?.theme?.id || 'current'}`;
    const updateQuantity = () => { this.setStatus(''); this.update(); };
    this.quantitySelector.addEventListener('quantity-selector:update', updateQuantity, { signal });
    this.quantityInput.addEventListener('input', updateQuantity, { signal });
    this.form.addEventListener('submit', (event) => this.addToCart(event), { signal });
    this.addEventListener('change', (event) => {
      this.setStatus('');
      this.update(event.target.name === 'barrier-size');
    }, { signal });
    this.querySelector('[data-reload-product]').addEventListener('click', () => this.loadProduct(), { signal });
    this.observer = new IntersectionObserver(([entry]) => {
      // Wait until the shopper has passed the main purchase button on every screen.
      // Being below the fold on arrival is not a reason to show a second CTA.
      this.sticky.hidden = entry.isIntersecting || entry.boundingClientRect.bottom > (entry.rootBounds?.top ?? 0);
    }, { threshold: 0 });
    this.observer.observe(this.submitButton);
    this.barObserver = new ResizeObserver(([entry]) => {
      if (entry.target.offsetHeight) document.documentElement.style.setProperty('--bw-cart-bar-height', `${entry.target.offsetHeight}px`);
    });
    this.barObserver.observe(this.sticky);
    this.loadProduct();
  }

  async loadProduct() {
    this.submitButton.disabled = true;
    this.stickySubmit.disabled = true;
    this.quantityFieldset.disabled = true;
    this.querySelectorAll('input[type="radio"]').forEach((input) => { input.disabled = true; });
    this.querySelector('[data-reload-product]').hidden = true;
    this.setStatus('');
    try {
      const response = await fetch(this.dataset.productUrl, { signal: this.controller.signal, cache: 'no-store' });
      if (!response.ok) throw new Error('Product could not be loaded.');
      this.product = await response.json();
      this.thirtyTabletProduct = null;
      let linkedProductFailure = null;
      if (this.dataset.thirtyProductUrl) {
        const linked = await loadLinkedProduct(this.dataset.thirtyProductUrl, { signal: this.controller.signal });
        this.thirtyTabletProduct = linked.product || null;
        if (linked.failure) {
          linkedProductFailure = { ...linked.failure, visibilityState: document.visibilityState, msSinceLoad: Math.round(performance.now()) };
        }
      }
      const linkedProductFailed = Boolean(linkedProductFailure);
      this.previewThirty = canPreviewThirtyTablets(this.dataset.previewThirty === 'true', window.Shopify?.theme?.role, this.product, Boolean(this.dataset.thirtyProductUrl));
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null'); } catch { /* Storage is optional. */ }
      const query = new URL(location.href).searchParams;
      const variantId = query.get('variant');
      let selectedSize = null;
      for (const size of ['60', '30']) {
        const variant = findSizeVariant(getSizeProduct(this.product, size, this.thirtyTabletProduct), size);
        const sizeInput = this.querySelector(`[name="barrier-size"][value="${size}"]`);
        const preview = size === '30' && this.previewThirty;
        sizeInput.disabled = !variant?.available && !preview;
        this.text(`[data-size-status="${size}"]`, variant && !variant.available ? `${size} tablets sold out` : '');
        // 60 tablets is always the default on load. Only an explicit ?variant= link selects 30.
        if (variant?.available && (!selectedSize || String(variant.id) === variantId)) selectedSize = size;
      }
      if (selectedSize) this.querySelector(`[name="barrier-size"][value="${selectedSize}"]`).checked = true;
      if (['one-time', 'subscription'].includes(saved?.purchase)) this.querySelector(`[name="barrier-purchase"][value="${saved.purchase}"]`).checked = true;
      const requestedPurchase = query.get('purchase');
      if (['one-time', 'subscription'].includes(requestedPurchase)) this.querySelector(`[name="barrier-purchase"][value="${requestedPurchase}"]`).checked = true;
      this.savedPlanId = saved?.planId;
      this.setStatus(linkedProductFailed ? 'The linked 30-tablet option could not load. Please check its availability and tablet count.' : '');
      this.querySelector('[data-reload-product]').hidden = !linkedProductFailed;
      this.update(true);
      this.dispatchEvent(new CustomEvent('barrier:product-loaded', { bubbles: true, detail: {
        productId: String(this.product.id), handle: this.product.handle, selection: this.selection,
        variantParam: variantId, purchaseParam: requestedPurchase, linkedProductFailed, linkedProductFailure,
      } }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.setStatus('Purchase options could not load. Please try again.', true);
      this.dispatchEvent(new CustomEvent('barrier:purchase-options-failed', { bubbles: true, detail: { message: error.message } }));
      this.querySelector('[data-reload-product]').hidden = false;
    }
  }

  text(selector, value) { this.querySelector(selector).textContent = value; }
  setStatus(message, showInBar = false) {
    this.status.textContent = message;
    this.text('[data-sticky-status]', showInBar ? message : '');
    this.querySelector('[data-sticky-status]').hidden = !showInBar;
  }
  money(cents, compact = false) { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency, minimumFractionDigits: compact && cents % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 }).format(cents / 100); }

  update(resetPlans = false) {
    if (!this.product) return;
    const size = this.querySelector('[name="barrier-size"]:checked').value;
    const product = getSizeProduct(this.product, size, this.thirtyTabletProduct);
    const variant = findSizeVariant(product, size);
    const plans = getSellingPlans(product, variant);
    const preview = size === '30' && this.previewThirty;
    this.text('[data-sticky-title]', preview ? 'Barrier BPC-GI (30 count)' : product.title);
    this.text('[data-size-duration]', `${size} days`);
    this.text('[data-guarantee]', `${size}-day money-back guarantee`);
    this.querySelector('[data-size-value]').hidden = !findSizeVariant(this.product, '60')?.available;
    if (resetPlans) {
      const previousPlan = this.planSelect.value || this.savedPlanId;
      this.planSelect.replaceChildren(...plans.map(({ plan }) => new Option(plan.name, String(plan.id))));
      if (plans.some(({ plan }) => String(plan.id) === String(previousPlan))) this.planSelect.value = String(previousPlan);
      if (variant) {
        if (this.quantitySelector.dataset.variantId !== String(variant.id)) this.quantitySelector.setCartQuantity(0);
        this.quantitySelector.dataset.variantId = String(variant.id);
        const rule = variant.quantity_rule || {};
        this.quantitySelector.updateConstraints(String(rule.min || 1), rule.max == null ? null : String(rule.max), String(rule.increment || 1));
      }
    }
    const subInput = this.querySelector('[name="barrier-purchase"][value="subscription"]');
    const onceInput = this.querySelector('[name="barrier-purchase"][value="one-time"]');
    subInput.disabled = this.busy || (!preview && (!variant?.available || !plans.length));
    onceInput.disabled = this.busy || (!preview && (!variant?.available || variant.requires_selling_plan || product.requires_selling_plan));
    if (!preview && subInput.checked && !plans.length && !onceInput.disabled) onceInput.checked = true;
    if (onceInput.checked && onceInput.disabled && !subInput.disabled) subInput.checked = true;
    const purchase = this.querySelector('[name="barrier-purchase"]:checked').value;
    const selection = getPurchaseSelection(product, size, purchase, this.planSelect.value, this.quantityInput.value);
    const quantity = selection?.quantity || 1;
    this.quantityFieldset.disabled = this.busy || !variant?.available;
    this.quantityInput.disabled = !variant?.available;
    const subscription = purchase === 'subscription';
    const selectedPlan = plans.find(({ plan }) => String(plan.id) === this.planSelect.value) || plans[0];
    this.querySelector('[data-plan-choice]').hidden = !subscription || plans.length < 2;
    this.planSelect.disabled = this.busy || !subscription || !plans.length;
    const cadence = getBillingCadence(selectedPlan?.plan);
    const subPrice = selectedPlan?.allocation.price;
    this.text('[data-once-price]', preview ? 'TBD' : variant && !variant.requires_selling_plan && !product.requires_selling_plan ? this.money(variant.price, true) : 'Unavailable');
    this.text('[data-sub-price]', preview ? 'TBD' : selectedPlan ? this.money(cadence.months ? subPrice / cadence.months : subPrice) : 'Unavailable');
    this.text('[data-sub-unit]', selectedPlan ? cadence.months ? '/ month' : '/ delivery' : '');
    this.text('[data-sub-order-price]', selectedPlan ? this.money(subPrice, true) : '');
    this.text('[data-sub-billing]', preview ? 'Pricing to be confirmed' : selectedPlan ? `billed ${cadence.label || 'per delivery'}` : '');
    this.text('[data-sub-supply]', `${size}-day supply with each delivery`);
    this.text('[data-bottle-count]', size);
    this.querySelectorAll('[data-servings]').forEach((servings) => { servings.textContent = size; });
    const saving = selectedPlan ? variant.price - selectedPlan.allocation.price : 0;
    this.selection = selection ? {
      variantId: selection.variantId, sellingPlanId: selection.sellingPlanId ? String(selection.sellingPlanId) : null,
      planName: subscription ? selectedPlan?.plan.name || null : null, size, purchase, quantity,
      price: selection.price / 100, saving: subscription ? saving / 100 : 0, sku: variant?.sku || null,
    } : null;
    const introductory = (selectedPlan?.allocation.price_adjustments || []).length > 1;
    this.text('[data-sub-compare]', saving > 0 ? this.money(variant.price, true) : '');
    this.querySelector('[data-sub-compare]').hidden = saving <= 0;
    this.text('[data-sub-offer]', preview ? '30-tablet preview' : saving > 0 ? `Save ${this.money(saving, true)} + free shipping` : selectedPlan ? 'Subscribe + free shipping' : 'Subscription unavailable');
    this.text('[data-savings]', preview ? 'Savings TBD' : saving > 0 ? `Save ${this.money(saving, true)} ${introductory ? 'on your first delivery' : '/ delivery'}` : '');
    this.querySelector('[data-savings-benefit]').hidden = saving <= 0 && !preview;
    this.querySelector('.bw-buy-benefits').hidden = !selectedPlan && !preview;
    this.text('[data-price]', preview ? '30-tablet preview. Pricing to be confirmed.' : selection ? `Quantity ${quantity}. ${size}-day supply each. ${this.money(selection.price * quantity)} ${subscription ? `billed ${cadence.label || 'per delivery'}.` : 'one time.'}` : 'Choose a valid quantity and purchase option.');
    let terms = '';
    if (subscription && selectedPlan) {
      const prices = selectedPlan.allocation.price_adjustments || [];
      const firstOrders = selectedPlan.plan.price_adjustments?.[0]?.order_count;
      if (prices.length > 1) terms = `${this.money(prices[0].price)}${firstOrders ? ` for the first ${firstOrders} order${firstOrders === 1 ? '' : 's'}` : ' initially'}, then ${this.money(prices[1].price)} per order. Renews automatically.`;
    }
    this.text('[data-terms]', terms);
    const selectionLabel = saving > 0 ? `Subscribe & save ${this.money(saving, true)}` : 'Subscribe';
    this.text('[data-selection]', subscription ? selectionLabel : '');
    this.querySelector('[data-selection]').hidden = !subscription;
    this.text('[data-sticky-price]', preview ? 'Preview only' : selection ? `${quantity > 1 ? `x${quantity} ` : ''}${this.money(selection.price, true)}` : 'Unavailable');
    this.text('[data-sticky-compare]', subscription && saving > 0 ? this.money(variant.price, true) : '');
    this.querySelector('[data-sticky-compare]').hidden = !subscription || saving <= 0;
    for (const button of [this.submitButton, this.stickySubmit]) {
      button.disabled = this.busy || !selection || !this.quantitySelector.canAddToCart().canAdd;
      button.textContent = this.busy ? 'Adding…' : 'Add to cart';
    }
    try { sessionStorage.setItem(this.storageKey, JSON.stringify({ size, purchase, planId: this.planSelect.value })); } catch { /* Optional enhancement. */ }
  }

  async addToCart(event) {
    event.preventDefault();
    if (this.busy || !this.product) return;
    const size = this.querySelector('[name="barrier-size"]:checked').value;
    const purchase = this.querySelector('[name="barrier-purchase"]:checked').value;
    if (size === '30' && this.previewThirty) return;
    const product = getSizeProduct(this.product, size, this.thirtyTabletProduct);
    const selection = getPurchaseSelection(product, size, purchase, this.planSelect.value, this.quantityInput.value);
    if (!selection || !this.quantitySelector.canAddToCart().canAdd) return;
    this.busy = true;
    this.form.querySelectorAll('fieldset').forEach((fieldset) => { fieldset.disabled = true; });
    this.update();
    this.setStatus('Adding to your cart…');
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(selection.variantId), quantity: selection.quantity }], promise: deferred.promise }));
    deferred.promise.catch(() => {});
    const root = this.dataset.rootUrl;
    let added = false;
    try {
      const item = { id: selection.variantId, quantity: selection.quantity };
      if (selection.sellingPlanId) item.selling_plan = selection.sellingPlanId;
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item], sections: ['cart-drawer-section'], sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'This option could not be added. Please check its availability.');
      added = true;
      const cartResponse = await fetch(`${root}cart.js`, { cache: 'no-store' });
      if (!cartResponse.ok) throw new Error('The cart could not be refreshed.');
      const cart = await cartResponse.json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, itemCount: cart.item_count, sections: result.sections, didError: false, source: 'barrier-purchase', productId: String(product.id) } });
      await customElements.whenDefined('theme-drawer');
      document.getElementById('cart-drawer')?.open();
      this.setStatus('Added to your cart.');
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CustomEvent('barrier:add-to-cart-failed', { bubbles: true, detail: { message: error.message, added } }));
      if (added) {
        this.setStatus('Added. Opening your cart…');
        window.location.assign(`${root}?cart=open`);
      } else {
        this.setStatus(`${error.message} If your connection dropped, check your cart before trying again.`, true);
      }
    } finally {
      this.busy = false;
      this.form.querySelectorAll('fieldset').forEach((fieldset) => { fieldset.disabled = false; });
      this.update();
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.observer?.disconnect();
    this.barObserver?.disconnect();
    document.documentElement.style.removeProperty('--bw-cart-bar-height');
  }
}
if (!customElements.get('barrier-purchase')) customElements.define('barrier-purchase', BarrierPurchase);
